import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { storeToRefs } from 'pinia'

import { useCommunityUser } from '@communities/community-user-layer/composables/useCommunityUser'

import { useMembersQueryStore } from '../stores/members.query-store'
import { useMembersUIStore } from '../stores/members.ui-store'

/**
 * Members Composable - Orchestrates member queries and UI state
 * This is the main entry point for member-related functionality
 *
 * Note: Do not call useGroupPageContext from here — useGroupInitialization already
 * uses useMembers, and useGroupPageContext calls useGroupInitialization, which would recurse.
 *
 * Admin/owner is read from community-user UI state (currentCommunityUser.role), which
 * useGroupInitialization keeps in sync with the current user’s group membership — no extra query.
 */
export const useMembers = (groupId: MaybeRefOrGetter<string | undefined>, locationId: MaybeRefOrGetter<string | undefined>) => {
  const queryStore = useMembersQueryStore(groupId)
  const uiStore = useMembersUIStore()
  const uiStoreRefs = storeToRefs(uiStore)

  const { ui: communityUserUi } = useCommunityUser()
  const isGroupAdmin = computed(() => {
    const role = communityUserUi.currentCommunityUser?.role
    return role != null && ['OWNER', 'ADMIN'].includes(String(role))
  })

  // Wrap query functions to use groupId and locationId from composable args
  const fetchGroupMembersListQuery = (filters?: MaybeRefOrGetter<string[]>, options?: { refetchOnMount?: boolean | 'always' }) => {
    return queryStore.fetchGroupMembersListQuery(groupId, locationId, filters, options)
  }

  const fetchGroupMembersDetailsQuery = (params: MaybeRefOrGetter<Record<string, unknown>>) => {
    return queryStore.fetchGroupMembersDetailsQuery(groupId, locationId, params)
  }

  const fetchRequestCountQuery = () => {
    return queryStore.fetchRequestCountQuery(groupId, locationId, isGroupAdmin)
  }

  const fetchMembershipQuestionsFormQuery = (formId: MaybeRefOrGetter<string>) => {
    return queryStore.fetchMembershipQuestionsFormQuery(groupId, locationId, formId)
  }

  const fetchGroupMembersInfiniteQuery = (baseParams: MaybeRefOrGetter<Record<string, unknown>>) => {
    return queryStore.fetchGroupMembersInfiniteQuery(groupId, locationId, baseParams)
  }

  // Wrap mutations to use groupId and locationId from composable args
  const updateGroupMember = (userId: string, role: string) => {
    return queryStore.updateGroupMemberMutation.mutateAsync({
      groupId: toValue(groupId),
      locationId: toValue(locationId),
      userId,
      role,
    })
  }

  const withdrawMemberInvitation = (userId: string, invitationId: string) => {
    return queryStore.withdrawMemberInvitationMutation.mutateAsync({
      groupId: toValue(groupId),
      locationId: toValue(locationId),
      userId,
      invitationId,
    })
  }

  const approveMemberRequest = (userId: string, role?: string) => {
    return queryStore.approveMemberRequestMutation.mutateAsync({
      groupId: toValue(groupId),
      locationId: toValue(locationId),
      userId,
      role,
    })
  }

  const rejectMemberRequest = (userId: string) => {
    return queryStore.rejectMemberRequestMutation.mutateAsync({
      groupId: toValue(groupId),
      locationId: toValue(locationId),
      userId,
    })
  }

  const submitMembershipAnswers = (formId: string, answers: (string | boolean[])[], contactId: string) => {
    return queryStore.submitMembershipAnswersMutation.mutateAsync({
      groupId: toValue(groupId)!,
      locationId: toValue(locationId),
      formId,
      contactId,
      answers,
    })
  }

  const banGroupMember = (userId: string, deleteContent?: boolean) => {
    return queryStore.banGroupMemberMutation.mutateAsync({
      groupId: toValue(groupId),
      locationId: toValue(locationId)!,
      userId,
      deleteContent,
    })
  }

  const unbanGroupMember = (userId: string) => {
    return queryStore.unbanGroupMemberMutation.mutateAsync({
      groupId: toValue(groupId),
      locationId: toValue(locationId)!,
      userId,
    })
  }

  const removeGroupMember = (userId: string, removeGamificationMeta?: boolean) => {
    return queryStore.removeGroupMemberMutation.mutateAsync({
      groupId: toValue(groupId),
      locationId: toValue(locationId)!,
      userId,
      removeGamificationMeta,
    })
  }

  const deleteMemberGamificationMeta = (userId: string) => {
    return queryStore.deleteMemberGamificationMetaMutation.mutateAsync({
      groupId: toValue(groupId),
      locationId: toValue(locationId)!,
      userId,
    })
  }

  return {
    // Query creation functions (wrapped to use groupId and locationId)
    fetchGroupMembersListQuery,
    fetchGroupMembersDetailsQuery,
    fetchRequestCountQuery,
    fetchMembershipQuestionsFormQuery,
    fetchGroupMembersInfiniteQuery,

    // Mutations (wrapped to use groupId and locationId)
    updateGroupMember,
    withdrawMemberInvitation,
    approveMemberRequest,
    rejectMemberRequest,
    submitMembershipAnswers,
    banGroupMember,
    unbanGroupMember,
    removeGroupMember,
    deleteMemberGamificationMeta,

    // Query keys (for manual invalidation)
    queryKeys: queryStore.queryKeys,

    // UI Store - State
    searchQuery: uiStoreRefs.searchQuery,
    roleFilter: uiStoreRefs.roleFilter,
    memberStatus: uiStoreRefs.memberStatus,
    currentPage: uiStoreRefs.currentPage,
    pageSize: uiStoreRefs.pageSize,
    showMembershipAnswersModal: uiStoreRefs.showMembershipAnswersModal,
    selectedMemberForReview: uiStoreRefs.selectedMemberForReview,
    showWithdrawInvitationModal: uiStoreRefs.showWithdrawInvitationModal,
    selectedMemberForWithdraw: uiStoreRefs.selectedMemberForWithdraw,
    isLoadingFilteredMembers: uiStoreRefs.isLoadingFilteredMembers,
    hasActiveFilters: uiStoreRefs.hasActiveFilters,

    // UI Store - Actions
    setSearchQuery: uiStore.setSearchQuery,
    setRoleFilter: uiStore.setRoleFilter,
    setMemberStatus: uiStore.setMemberStatus,
    setCurrentPage: uiStore.setCurrentPage,
    setPageSize: uiStore.setPageSize,
    openMembershipAnswersModal: uiStore.openMembershipAnswersModal,
    closeMembershipAnswersModal: uiStore.closeMembershipAnswersModal,
    openWithdrawInvitationModal: uiStore.openWithdrawInvitationModal,
    closeWithdrawInvitationModal: uiStore.closeWithdrawInvitationModal,
    setLoadingFilteredMembers: uiStore.setLoadingFilteredMembers,
    resetFilters: uiStore.resetFilters,
    resetState: uiStore.resetState,
  }
}
